import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { css, styled } from 'styled-components';

import { Box } from 'components/Box/Box';
import { colors, cssBreakpoints } from 'theme/theme';

import { LargeRadioButton } from './LargeRadioButton';
import { RadioButton } from './RadioButton';

export const RadioContainer = styled.div<{
  $columns?: number | boolean | undefined;
  $hasError?: boolean;
}>`
  display: flex;
  flex-wrap: wrap;
  ${(props) =>
    props.$columns
      ? css`
          margin: 0 -6px;
        `
      : css`
          flex-direction: column;
        `};
  justify-content: flex-start;
  border-radius: 4px;

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    flex-direction: row;
  }
  ${(props) =>
    props.$hasError &&
    css`
      border: solid 1px ${colors.alertRed};
      box-shadow: 0 0 0 1px ${colors.alertRed};
      padding-right: 6px;
      padding-left: 6px;
      margin: 0 -${props.$columns ? 12 : 6}px;
    `};
`;

export type RadioButtonsProps = {
  id?: string;
  name?: string;
  value?: string | boolean | number | null;
  buttons: {
    label: ReactNode;
    value: string | boolean | number;
    disabled?: boolean;
  }[];
  large?: boolean;
  alignTop?: boolean;
  columns?: number | number[];
  hasError?: boolean;
  onChange: (value: string | boolean | number | null) => void;
  disabled?: boolean;
  required?: boolean;
  canDeselect?: boolean;
  hideRadioButtons?: boolean;
  'data-qa-id'?: string;
  noMinHeight?: boolean;
  color?: string;
};

export function RadioButtons({
  id,
  name,
  value,
  buttons,
  large,
  alignTop,
  columns,
  hasError,
  onChange,
  disabled,
  required,
  canDeselect,
  hideRadioButtons,
  noMinHeight,
  color,
  ...props
}: RadioButtonsProps) {
  const RadioButtonComponent = useMemo(() => {
    if (large) return LargeRadioButton;
    return RadioButton;
  }, [large]);

  const colCount =
    columns instanceof Array ? columns.reduce((sum, i) => sum + i, 0) : columns;
  return (
    <RadioContainer
      $columns={colCount}
      $hasError={hasError}
      data-qa-id={props['data-qa-id']}
    >
      {buttons.map(
        (
          { label: inputLabel, value: inputValue, disabled: inputDisabled },
          index,
        ) => (
          <Box
            key={inputValue.toString()}
            pr={!columns && index !== buttons.length - 1 ? '50px' : 0}
            // @ts-expect-error TS(2322): Type 'false | "6px" | undefined' is not assignable... Remove this comment to see the full error message
            p={Boolean(columns) && '6px'}
            display="flex"
            width={
              colCount && [
                1,
                1,
                (columns instanceof Array && columns[index]
                  ? columns[index]
                  : 1) / colCount,
              ]
            }
          >
            <RadioButtonComponent
              name={name}
              alignTop={large ? alignTop : undefined}
              label={inputLabel}
              id={
                index === 0
                  ? id || props['data-qa-id']
                  : `${id || props['data-qa-id']}-${inputValue.toString()}`
              }
              data-qa-id={`${props['data-qa-id']}-${inputValue.toString()}`}
              onChange={onChange}
              onClick={() => {
                if (!canDeselect) return;
                if (value === inputValue) {
                  onChange(null);
                }
              }}
              value={inputValue}
              checked={Boolean(value === inputValue)}
              data-checked={Boolean(value === inputValue)}
              disabled={Boolean(disabled || inputDisabled)}
              required={required}
              hideRadioButtons={hideRadioButtons}
              noMinHeight={noMinHeight}
              color={color}
            />
          </Box>
        ),
      )}
    </RadioContainer>
  );
}
